import { Logger } from "@amzn/xb-hooks";
import type { AwsRum } from "aws-rum-web";
import { isAwsRum } from "./rumTypes";
import { XBError, XpsError } from "../live-component/federationUtils";

declare global {
  interface Window {
    cwr?: AwsRum;
  }
}

const getRumClient = (): AwsRum | undefined => {
  const client = window.cwr;

  if (isAwsRum(client)) {
    return client;
  }
};

/**
 * Metrics recorded by rum should correlate to the xb metric defintions in the AwsTcCdkConstructs.
 * Otherwise they won't be picked up correctly by cloudwatch.
 * @see https://code.amazon.com/packages/AwsTcCdkConstructs/blobs/mainline/--/lib/constructs/ExperienceBridge/xbRumMetricDefinitions.ts
 */
export const createRumLogger = (xbInfo: { xbAppName: string; xpsName?: string; componentName?: string }) => {
  const rum = getRumClient();

  const eventBase = {
    version: "1.0.0",
    ...xbInfo,
  };

  /**
   * We don't expect this to happen, and if it does it is hopefully transient.
   * We've already lost visibility into insights so we'll avoid dumping
   * logging spam to make issues worse...
   */
  if (rum === undefined) {
    console.error("CWR not initialized.");
    return {
      count: () => {},
      time: () => {},
      info: () => {},
      debug: () => {},
      warn: () => {},
      error: () => {},
      fatal: () => {},
    } as Logger;
  } else {
    // TODO add XPS controlled log-level capability
    return {
      count: (metricName, count = 1) =>
        rum.recordEvent("xb.custom_metric", {
          ...eventBase,
          metricName,
          count,
        }),
      time: (metricName, elapsedTime) =>
        rum.recordEvent("xb.timer_metric", {
          ...{
            version: "1.0.0",
            ...xbInfo,
          },
          metricName,
          time: elapsedTime,
        }),
      info: () => {}, // currently not implemented for production
      debug: () => {}, // currently not implemented for production
      warn: (message: any, ...rest: object[]) =>
        rum.recordEvent("xb.error_event", {
          ...eventBase,
          message,
          errorCount: 1,
          severity: "WARN",
          extraInfo: { ...rest },
        }),
      error: (message: string, error: Error & { origin?: "XPS" | "XB"; name?: string }, ...rest) => {
        rum.recordEvent("xb.error_event", {
          ...eventBase,
          message,
          errorOrigin: error?.origin ?? "",
          errorName: error?.name ?? "",
          errorCount: 1,
          severity: "ERROR",
          ...rest,
        });
      },
      fatal: (message: any, ...rest) =>
        rum.recordEvent("xb.error_event", {
          ...eventBase,
          message,
          errorCount: 1,
          severity: "FATAL",
          ...rest,
        }),
    } as Logger;
  }
};
